import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  CheckCircle,
  Heart,
  Leaf,
  MapPin,
  Shield,
  Users,
  IndianRupee,
  Sparkles,
  X,
  Minus,
  Plus,
} from "lucide-react";
import {
  fetchPublicTreatmentRetreatPackages,
  type PublicTreatmentRetreatPackage,
} from "./treatmentRetreatPublicApi";
import { SEO } from "../../SEO";

const toArray = <T,>(value: T[] | null | undefined): T[] =>
  Array.isArray(value) ? value : [];

const formatCurrency = (amount?: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));

const getDisplayPrice = (pkg: PublicTreatmentRetreatPackage) => {
  const prices = toArray(pkg.pricingOptions)
    .filter((opt) => typeof opt.price === "number" && opt.price > 0)
    .map((opt) => opt.price);

  if (prices.length > 0) {
    return `From ${formatCurrency(Math.min(...prices))}`;
  }

  if (pkg.price && pkg.price > 0) {
    return formatCurrency(pkg.price);
  }

  return formatCurrency(0);
};

const WellnessAndNaturopathy = () => {
  const navigate = useNavigate();
  const [packages, setPackages] = useState<PublicTreatmentRetreatPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingPackage, setBookingPackage] = useState<PublicTreatmentRetreatPackage | null>(null);
  const [selectedQuantities, setSelectedQuantities] = useState<Record<string, number>>({});

  useEffect(() => {
    const loadPackages = async () => {
      try {
        const response = await fetchPublicTreatmentRetreatPackages();
        console.log("Fetched packages:", response);
        if (response?.success && Array.isArray(response.data)) {
          setPackages(response.data);
        } else {
          setPackages([]);
        }
      } finally {
        setLoading(false);
      }
    };
    loadPackages();
  }, []);

  const initializeQuantities = (pkg: PublicTreatmentRetreatPackage) => {
    const initialRecord: Record<string, number> = {};
    toArray(pkg.pricingOptions).forEach((option) => {
      initialRecord[option.type] = 0;
    });
    return initialRecord;
  };

  const getBookingBaseTotal = (pkg: PublicTreatmentRetreatPackage | null, quantities: Record<string, number>) => {
    if (!pkg) return 0;
    const options = toArray(pkg.pricingOptions);
    if (options.length > 0) {
      return options.reduce((sum, option) => {
        const qty = quantities[option.type] ?? 0;
        return sum + option.price * qty;
      }, 0);
    }
    return (pkg.price || 3500) * (quantities["Standard"] ?? 1);
  };

  const getPackageCommissionRate = (pkg: PublicTreatmentRetreatPackage | null) => {
    if (pkg?.commissionPercentage !== null && pkg?.commissionPercentage !== undefined && !isNaN(Number(pkg.commissionPercentage))) {
      return Number(pkg.commissionPercentage);
    }
    const optComm = (toArray(pkg?.pricingOptions)[0] as any)?.commissionPercentage;
    if (optComm !== null && optComm !== undefined && !isNaN(Number(optComm))) {
      return Number(optComm);
    }
    return 0;
  };

  const getBookingCommissionFee = (pkg: PublicTreatmentRetreatPackage | null, quantities: Record<string, number>) => {
    const base = getBookingBaseTotal(pkg, quantities);
    const rate = getPackageCommissionRate(pkg);
    if (rate > 0 && base > 0) {
      return Math.round(((base * rate) / 100) * 100) / 100;
    }
    return 0;
  };

  const getBookingTotal = (pkg: PublicTreatmentRetreatPackage | null, quantities: Record<string, number>) => {
    const base = getBookingBaseTotal(pkg, quantities);
    const fee = getBookingCommissionFee(pkg, quantities);
    return Math.round((base + fee) * 100) / 100;
  };

  const openBookingModal = (pkg: PublicTreatmentRetreatPackage) => {
    setBookingPackage(pkg);
    setSelectedQuantities(initializeQuantities(pkg));
    setIsBookingModalOpen(true);
  };

  const closeBookingModal = () => {
    setIsBookingModalOpen(false);
    setBookingPackage(null);
    setSelectedQuantities({});
  };

  const updateQuantity = (type: string, delta: number) => {
    setSelectedQuantities((prev) => {
      const nextQuantity = Math.max(0, (prev[type] ?? 0) + delta);
      return {
        ...prev,
        [type]: nextQuantity,
      };
    });
  };

  const handleContinueToPreview = () => {
    if (!bookingPackage) return;
    const baseTotal = getBookingBaseTotal(bookingPackage, selectedQuantities);
    if (baseTotal <= 0) return;
    const rate = getPackageCommissionRate(bookingPackage);
    const commFee = getBookingCommissionFee(bookingPackage, selectedQuantities);
    const finalTotal = Math.round((baseTotal + commFee) * 100) / 100;

    navigate("/luxury-wellness-retreats/booking-preview", {
      state: {
        packageData: bookingPackage,
        selectedQuantities,
        baseAmount: baseTotal,
        commissionPercentage: rate,
        commissionAmount: commFee,
        totalAmount: finalTotal,
      },
    });
    closeBookingModal();
  };

  const healingCategories = [
    {
      id: "Natural Healing",
      title: "Natural Healing",
      icon: <Leaf className="h-5 w-5" />,
      description:
        "Ayurvedic consultations, therapeutic diets, natural detox planning, and plant-led healing support.",
      treatments: [
        "Ayurvedic consultations",
        "Customized diet plans",
        "Herbal therapy",
        "Therapeutic diet plans",
        "Detox support",
      ],
    },
    {
      id: "Restorative Therapies",
      title: "Restorative Therapies",
      icon: <Heart className="h-5 w-5" />,
      description:
        "Mind-body recovery using yoga, breathwork, physiotherapy support, and calming therapies.",
      treatments: [
        "Mind therapy",
        "Physiotherapy and yoga",
        "Breathing techniques",
        "Stress support",
        "Energy healing",
      ],
    },
    {
      id: "Holistic Wellness",
      title: "Who Is It For?",
      icon: <Shield className="h-5 w-5" />,
      description:
        "Useful for chronic pain, digestive trouble, poor sleep, stress, anxiety, and lifestyle imbalance.",
      treatments: [
        "Chronic pain relief",
        "Digestive health",
        "Anxiety and stress",
        "Sleep support",
        "Weight management",
      ],
    },
  ];
  const filteredPackages = packages.filter((pkg) => {
    if (selectedCategory === "All") return true;

    const categoryLower = String(pkg.category || "").toLowerCase();
    const subCategoryLower = String(pkg.subCategory || "").toLowerCase();
    const treatmentTypeLower = String(pkg.treatmentType || "").toLowerCase();
    const specializationLower = String(pkg.specialization || "").toLowerCase();
    const selectedLower = selectedCategory.toLowerCase();

    if (categoryLower.includes(selectedLower) || subCategoryLower.includes(selectedLower)) {
      return true;
    }

    if (selectedCategory === "Natural Healing") {
      return (
        categoryLower.includes("natural") ||
        categoryLower.includes("healing") ||
        categoryLower.includes("ayurveda") ||
        subCategoryLower.includes("natural") ||
        subCategoryLower.includes("healing") ||
        subCategoryLower.includes("ayurveda") ||
        treatmentTypeLower.includes("ayurveda") ||
        treatmentTypeLower.includes("naturopathy") ||
        specializationLower.includes("ayurveda") ||
        specializationLower.includes("naturopathy")
      );
    }

    if (selectedCategory === "Restorative Therapies") {
      return (
        categoryLower.includes("restorative") ||
        categoryLower.includes("therapy") ||
        categoryLower.includes("yoga") ||
        categoryLower.includes("physio") ||
        subCategoryLower.includes("restorative") ||
        subCategoryLower.includes("therapy") ||
        subCategoryLower.includes("yoga") ||
        subCategoryLower.includes("physio") ||
        treatmentTypeLower.includes("yoga") ||
        treatmentTypeLower.includes("therapy") ||
        specializationLower.includes("yoga") ||
        specializationLower.includes("therapy")
      );
    }

    if (selectedCategory === "Holistic Wellness") {
      return (
        categoryLower.includes("holistic") ||
        categoryLower.includes("wellness") ||
        subCategoryLower.includes("holistic") ||
        subCategoryLower.includes("wellness") ||
        treatmentTypeLower.includes("wellness") ||
        specializationLower.includes("wellness")
      );
    }

    return false;
  });

  return (
    <div className="min-h-screen bg-[#F5F0E3] font-sans text-[#15302B]">
      <SEO
        title="Luxury Wellness Retreats & Naturopathy Packages | MetaFit Wellness"
        description="Book curated luxury wellness retreats and naturopathy packages across India. Experience authentic rejuvenation, holistic healing, and verified wellness stays."
        canonical="/luxury-wellness-retreats"
        keywords="wellness retreats, luxury retreats, naturopathy packages, holistic rejuvenation, wellness resort, MetaFit Wellness"
      />

      {/* ── HERO ───────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-b border-[#15302B]/8 bg-[#F5F0E3]">
        {/* subtle dot pattern */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "radial-gradient(circle, #15302B 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        />

        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-[#3F6652]/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-[#E6A93F]/15 blur-3xl" />
        
        <div className="relative mx-auto max-w-[1180px] px-5 py-4 sm:px-8 text-center sm:text-left">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <span className="inline-flex items-center gap-1.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-[#3F6652]">
                <Sparkles className="h-2.5 w-2.5" />
                Curated Naturopathy Wellness Stays
              </span>
              <h1 className="mt-0.5 font-serif text-lg font-semibold leading-tight text-[#15302B] sm:text-xl md:text-2xl">
                Retreats designed to help you <span className="italic text-[#3F6652]">feel whole again.</span>
              </h1>
              <p className="mt-1 text-[11px] leading-relaxed text-slate-500 max-w-xl">
                Explore live naturopathy packages from verified wellness centers. Compare stays, pricing, and availability — all at a glance.
              </p>
            </div>
            
            {/* Quick metrics badges */}
            <div className="flex flex-wrap justify-center sm:justify-start gap-1.5 flex-shrink-0">
              <span className="rounded-lg border border-[#15302B]/10 bg-[#FFFDF7]/70 px-2.5 py-1 text-[10px] font-medium text-slate-600 backdrop-blur-sm">
                <strong className="text-[#15302B] font-semibold">{packages.length}</strong> stays
              </span>
              <span className="rounded-lg border border-[#15302B]/10 bg-[#FFFDF7]/70 px-2.5 py-1 text-[10px] font-medium text-slate-600 backdrop-blur-sm">
                <strong className="text-[#15302B] font-semibold">{packages.filter((p) => p.markAsTrending).length}</strong> trending
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── PACKAGE LISTING ────────────────────────────────────────── */}
      <section className="bg-[#FFFDF7] py-6 sm:py-8">
        <div className="mx-auto max-w-[1180px] px-5 sm:px-8">

          {/* Compact Filter Tabs */}
          <div className="mb-5 flex flex-col gap-4 border-b border-[#15302B]/10 pb-4 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
            <div className="flex-1 min-w-0">
              <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.2em] text-[#3F6652]">
                Available Stays & Programs
              </span>
              {/* Dynamic Category Text */}
              {(() => {
                const activeCat = healingCategories.find((c) => c.id === selectedCategory);
                return (
                  <p className="mt-1 text-[11px] leading-relaxed text-slate-500 max-w-xl">
                    {activeCat 
                      ? `${activeCat.title}: ${activeCat.description}`
                      : "Explore all naturopathy stays, compare wellness therapies, pricing, and availability at a glance."}
                  </p>
                );
              })()}
            </div>

            {/* Pill Selector */}
            <div className="flex flex-wrap gap-1 rounded-xl bg-[#F5F0E3] p-1 border border-[#15302B]/5 flex-shrink-0 self-start lg:self-center">
              {/* All tab */}
              <button
                onClick={() => setSelectedCategory("All")}
                className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold transition-all duration-300 ${
                  selectedCategory === "All"
                    ? "bg-[#15302B] text-white shadow-sm"
                    : "text-[#3B5750] hover:bg-[#15302B]/5"
                }`}
              >
                <Leaf className="h-3 w-3" />
                <span>All</span>
              </button>

              {healingCategories.map((cat) => {
                const isActive = selectedCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold transition-all duration-300 ${
                      isActive
                        ? "bg-[#15302B] text-white shadow-sm"
                        : "text-[#3B5750] hover:bg-[#15302B]/5"
                    }`}
                  >
                    <span className="flex-shrink-0">{cat.icon}</span>
                    <span>{cat.title}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* states */}
          {loading ? (
            <div className="grid items-start gap-5 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[320px] animate-pulse rounded-2xl border border-slate-200 bg-white"
                />
              ))}
            </div>
          ) : filteredPackages.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-emerald-200 bg-white px-8 py-14 text-center">
              <Leaf className="mx-auto h-10 w-10 text-emerald-300 animate-bounce" />
              <h3 className="mt-4 text-base font-semibold text-[#15302B]">
                No packages match this category
              </h3>
              <p className="mt-1 text-xs text-slate-500">
                Please select another wellness category or view all programs.
              </p>
            </div>
          ) : (
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {packages.map((pkg) => {
                const previewImage = toArray(pkg.uploadImages)[0];
                return (
                  <article
                    key={pkg._id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openBookingModal(pkg)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openBookingModal(pkg);
                      }
                    }}
                    className="group flex cursor-pointer flex-col overflow-hidden rounded-[20px] border border-[#15302B]/10 bg-white shadow-[0_10px_28px_rgba(21,48,43,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-[#3F6652]/30 hover:shadow-[0_16px_40px_rgba(21,48,43,0.1)] focus:outline-none focus:ring-2 focus:ring-[#3F6652]/30 focus:ring-offset-2"
                  >
                    {/* image / placeholder */}
                    <div className="relative aspect-video w-full overflow-hidden bg-[#FAF7F2]">
                      {previewImage ? (
                        <>
                          <img
                            src={previewImage}
                            alt=""
                            aria-hidden="true"
                            className="absolute inset-0 h-full w-full scale-110 object-cover blur-md opacity-35 select-none pointer-events-none"
                          />
                          <img
                            src={previewImage}
                            alt={pkg.packageName || "Wellness Package"}
                            className="relative z-10 h-full w-full object-contain"
                          />
                        </>
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <Leaf className="h-10 w-10 text-emerald-300 animate-pulse" />
                        </div>
                      )}

                      {/* badges */}
                      <div className="absolute inset-x-0 top-0 flex items-start justify-between p-2.5">
                        <span className="rounded-full border border-white/20 bg-[#15302B]/75 px-2.5 py-1 text-[9px] font-medium text-white backdrop-blur-md">
                          {pkg.availableSlots || 0} slots available
                        </span>
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-[10px] font-medium backdrop-blur-sm ${
                            pkg.markAsTrending
                              ? "bg-[#E6A93F] text-[#15302B]"
                              : "bg-white/80 text-slate-600"
                          }`}
                        >
                          {pkg.markAsTrending ? "Trending" : "Listed"}
                        </span>
                      </div>
                    </div>

                    {/* body */}
                    <div className="flex flex-col p-3.5 sm:p-4">

                      {/* name + price */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="truncate font-serif text-lg font-semibold text-[#15302B]">
                            {pkg.packageName || "Wellness Package"}
                          </h3>
                          <p className="mt-0.5 truncate text-[11px] text-slate-500">
                            {[pkg.treatmentType, pkg.specialization]
                              .filter(Boolean)
                              .slice(0, 2)
                              .join(" · ") || "Wellness therapies"}
                          </p>
                        </div>
                        <div className="flex-shrink-0 rounded-lg bg-[#F5F0E3] px-2.5 py-1.5 text-right">
                          <p className="text-[8px] font-semibold uppercase tracking-wide text-[#3F6652]">
                            From
                          </p>
                          <p className="font-serif text-[13px] font-semibold text-[#15302B]">
                            {getDisplayPrice(pkg)}
                          </p>
                        </div>
                      </div>

                      {/* meta grid */}
                      <div className="mt-2.5 grid grid-cols-2 gap-1.5">
                        {[
                          {
                            icon: <MapPin className="h-3 w-3" />,
                            label: "Location",
                            value: [pkg.city, pkg.state].filter(Boolean).join(", ") || "—",
                          },
                          {
                            icon: <Calendar className="h-3 w-3" />,
                            label: "Availability",
                            value:
                              pkg.availabilityFrom && pkg.availabilityTo
                                ? `${pkg.availabilityFrom} - ${pkg.availabilityTo}`
                                : "Flexible",
                          },
                          {
                            icon: <Users className="h-3 w-3" />,
                            label: "Slots",
                            value: String(pkg.availableSlots ?? 0),
                          },
                          {
                            icon: <Sparkles className="h-3 w-3" />,
                            label: "Type",
                            value: pkg.bookingType || "Recurring",
                          },
                        ].map((m) => (
                          <div
                            key={m.label}
                            className="min-h-[46px] rounded-lg border border-[#15302B]/5 bg-[#F8F6EF] px-2 py-1 flex flex-col justify-center"
                          >
                            <div className="flex items-center gap-1 text-[8px] font-semibold uppercase tracking-wider text-slate-400">
                              {m.icon}
                              {m.label}
                            </div>
                            <p className="mt-0.5 line-clamp-1 text-[10px] font-medium leading-tight text-slate-700">
                              {m.value}
                            </p>
                          </div>
                        ))}
                      </div>

                      {/* category tags */}
                      {(pkg.category || pkg.subCategory) && (
                        <div className="mt-2.5 flex max-h-7 flex-wrap gap-1 overflow-hidden">
                          {[pkg.category, pkg.subCategory].filter(Boolean).map((d) => (
                            <span
                              key={String(d)}
                              className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[9px] font-medium text-emerald-700"
                            >
                              {d}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* footer */}
                      <div className="mt-3.5 flex items-center justify-between border-t border-slate-100 pt-2.5">
                        {(pkg.sessionPrice ?? 0) > 0 ? (
                          <div>
                            <p className="text-[9px] font-medium uppercase tracking-wider text-slate-400">
                              Session price
                            </p>
                            <div className="mt-0.5 flex items-center gap-0.5 text-[13px] font-semibold text-slate-800">
                              <IndianRupee className="h-3 w-3 text-emerald-600" />
                              {formatCurrency(pkg.sessionPrice)}
                              <span className="text-[10px] font-normal text-slate-400">
                                /session
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex-1" />
                        )}

                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openBookingModal(pkg);
                            }}
                            className="inline-flex items-center justify-center rounded-full bg-[#15302B] px-3 py-2 text-[8px] font-semibold uppercase tracking-[0.1em] text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-[#2C4A3B] active:scale-[0.98]"
                          >
                            Book Package
                          </button>
                          <Link
                            to={`/luxury-wellness-retreats/${pkg._id}`}
                            onClick={(event) => event.stopPropagation()}
                            className="inline-flex items-center gap-1 rounded-full border border-[#15302B]/20 bg-white px-3 py-2 text-[8px] font-semibold uppercase tracking-[0.08em] text-[#15302B] transition-colors hover:bg-[#F5F0E3] active:scale-[0.98]"
                          >
                            View Details
                            <ArrowRight className="h-3 w-3" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
      {isBookingModalOpen && bookingPackage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#15302B]/65 px-4 py-6 backdrop-blur-sm">
          <div className="w-full max-w-md overflow-hidden rounded-[28px] border border-white/20 bg-[#FFFDF7] shadow-2xl">
            <div className="h-1.5 bg-gradient-to-r from-[#3F6652] via-[#E6A93F] to-[#3F6652]" />
            <div className="flex items-center justify-between border-b border-[#15302B]/10 px-6 py-5">
              <div>
                <h2 className="font-serif text-2xl font-semibold text-[#15302B]">Book your retreat</h2>
                <p className="mt-1 text-xs text-[#3B5750]">Select participants for each option</p>
              </div>
              <button
                onClick={closeBookingModal}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#15302B]/5 text-[#3B5750] hover:bg-[#15302B]/10"
                aria-label="Close booking modal"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 p-6">
              <div className="rounded-2xl border border-[#15302B]/8 bg-[#F5F0E3] p-4">
                <h3 className="font-serif text-lg font-semibold text-[#15302B]">{bookingPackage.packageName}</h3>
                <p className="mt-1 text-xs text-[#3B5750]">
                  {bookingPackage.specialization || bookingPackage.treatmentType}
                </p>
              </div>

              <div className="max-h-56 space-y-3 overflow-y-auto">
                {toArray(bookingPackage.pricingOptions).map((option) => {
                  const quantity = selectedQuantities[option.type] ?? 0;
                  return (
                    <div key={option.type} className="rounded-2xl border border-[#15302B]/10 bg-white p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{option.type}</p>
                          <p className="mt-1 text-sm text-slate-500">{formatCurrency(option.price)} per person</p>
                        </div>
                        <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white p-1">
                          <button
                            type="button"
                            onClick={() => updateQuantity(option.type, -1)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="min-w-[2rem] text-center text-sm font-semibold">{quantity}</span>
                          <button
                            type="button"
                            onClick={() => updateQuantity(option.type, 1)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {(() => {
                const base = getBookingBaseTotal(bookingPackage, selectedQuantities);
                const rate = getPackageCommissionRate(bookingPackage);
                const commFee = getBookingCommissionFee(bookingPackage, selectedQuantities);
                const total = Math.round((base + commFee) * 100) / 100;
                return (
                  <div className="space-y-1.5 rounded-2xl border border-[#3F6652]/15 bg-[#3F6652]/5 p-3.5 text-xs">
                    <div className="flex items-center justify-between text-slate-600">
                      <span>Base Amount</span>
                      <span className="font-mono font-medium text-slate-900">{formatCurrency(base)}</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span>Taxes & Platform Fees</span>
                      {rate > 0 ? (
                        <span className="font-semibold text-[#2D6A4F]">
                          Included{commFee > 0 ? ` (+${formatCurrency(commFee)})` : ''}
                        </span>
                      ) : (
                        <span className="font-semibold text-[#B91C1C]">
                          Not Included
                        </span>
                      )}
                    </div>
                    <div className="border-t border-[#3F6652]/15 pt-1.5 flex items-center justify-between font-bold text-sm">
                      <span className="text-slate-900">Estimated Total</span>
                      <span className="font-mono text-emerald-800">{formatCurrency(total)}</span>
                    </div>
                  </div>
                );
              })()}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={closeBookingModal}
                  className="flex-1 rounded-full border border-[#15302B]/25 px-4 py-3 text-xs font-semibold text-[#15302B] hover:bg-[#15302B]/5"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={getBookingTotal(bookingPackage, selectedQuantities) <= 0}
                  onClick={handleContinueToPreview}
                  className="inline-flex flex-[1.5] items-center justify-center rounded-full bg-[#15302B] px-4 py-3 text-xs font-semibold text-white shadow-lg hover:bg-[#2C4A3B] disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  Continue to preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WellnessAndNaturopathy;
